import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { HorizontalBlurShader } from 'three/examples/jsm/shaders/HorizontalBlurShader.js';
import { VerticalBlurShader } from 'three/examples/jsm/shaders/VerticalBlurShader.js';
import type { Object3D, Scene, WebGLRenderer } from 'three';

/*
 * Portions of this file are adapted from:
 * https://github.com/google/model-viewer/blob/main/packages/model-viewer/src/three-components/Shadow.ts
 * Copyright 2022 Google LLC, licensed under Apache License 2.0.
 */

const BLUR_RADIUS_PX = 4;
const SHADOW_PLANE_NAME = 'spotar-contact-shadow-plane';
const SHADOW_RESOLUTION_SCALE = 500;
const SHADOW_PLANE_OUTER_MARGIN = 0.2;
const CONTACT_SHADOW_LAYER_BASE = 1;
let shadowReceivingObjects: Object3D[] = [];

let scene: Scene | null = null;
let renderer: WebGLRenderer | null = null;
let floor: THREE.Mesh | null = null;
let camera: THREE.OrthographicCamera | null = null;
let renderTarget: THREE.WebGLRenderTarget | null = null;
let blurTarget: THREE.WebGLRenderTarget | null = null;
let blurQuad: FullScreenQuad | null = null;
let horizontalBlur: THREE.ShaderMaterial | null = null;
let verticalBlur: THREE.ShaderMaterial | null = null;
let depthMaterial: THREE.MeshDepthMaterial | null = null;
let shadowSoftness = 0;

export type ContactShadowOptions = {
  scene: Scene;
  modelRoot: Object3D;
  renderer?: WebGLRenderer;
  intensity?: number;
  softness?: number;
  floorY?: number;
};

export function addContactShadowPlanes({
  scene: inputScene,
  modelRoot,
  renderer: inputRenderer,
  intensity = .5,
  softness = 2,
  floorY = 0,
}: ContactShadowOptions) {
  if (!inputRenderer) return;

  
  shadowReceivingObjects = collectShadowTargets(modelRoot);

  for (let i = 0; i < shadowReceivingObjects.length; i++) {
    const model = shadowReceivingObjects[i];
    addContactShadowPlane(model, inputScene, inputRenderer, intensity, softness, floorY, CONTACT_SHADOW_LAYER_BASE + i);
  }
}

export function addContactShadowPlane(
  model: Object3D,
  inputScene: Scene,
  inputRenderer: WebGLRenderer,
  intensity: number,
  softness: number,
  floorY: number,
  shadowLayer: number,
) {
  removeExistingShadowPlane(model);

  const bounds = computeWorldBounds(model);
  if (bounds.isEmpty()) return;

  const size = bounds.getSize(new THREE.Vector3()).add(new THREE.Vector3(SHADOW_PLANE_OUTER_MARGIN, 0, SHADOW_PLANE_OUTER_MARGIN));
  const center = bounds.getCenter(new THREE.Vector3());
  if (size.x <= 0 || size.z <= 0) return;

  model.updateMatrixWorld(true);
  const localCenter = model.worldToLocal(center.clone());
  const floorLocalY = model.worldToLocal(new THREE.Vector3(center.x, floorY, center.z)).y;
  const shadowHeight = bounds.max.y - floorY;

  const shadowTextureSizeX = size.x * SHADOW_RESOLUTION_SCALE;
  const shadowTextureSizeZ = size.z * SHADOW_RESOLUTION_SCALE;
  const renderTargetParams: THREE.RenderTargetOptions = { format: THREE.RGBAFormat };

  scene = inputScene;
  renderer = inputRenderer;
  shadowSoftness = softness;
  renderTarget = new THREE.WebGLRenderTarget(shadowTextureSizeX, shadowTextureSizeZ, renderTargetParams);
  blurTarget = new THREE.WebGLRenderTarget(shadowTextureSizeX, shadowTextureSizeZ, renderTargetParams);
  horizontalBlur = new THREE.ShaderMaterial(HorizontalBlurShader);
  verticalBlur = new THREE.ShaderMaterial(VerticalBlurShader);
  blurQuad = new FullScreenQuad(horizontalBlur);

  const shadowMaterial = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: intensity,
    depthWrite: false,
  });
  shadowMaterial.map = renderTarget.texture;

  floor = new THREE.Mesh(new THREE.PlaneGeometry(), shadowMaterial);
  floor.name = SHADOW_PLANE_NAME;
  floor.raycast = () => {};
  floor.userData.contactShadowRenderTarget = renderTarget;
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(center.x, floorY + 0.001, center.z);
  floor.scale.set(size.x, size.z, 1);
  model.attach(floor);

  camera = new THREE.OrthographicCamera(size.x / 2, -size.x / 2, size.z / 2, -size.z / 2);
  camera.rotation.x = Math.PI / 2;
  camera.rotation.z = Math.PI;
  camera.near = 0;
  camera.far = Math.max(1, shadowHeight);
  camera.position.copy(model.localToWorld(new THREE.Vector3(localCenter.x, floorLocalY, localCenter.z)));
  camera.updateProjectionMatrix();
  model.attach(camera);

  enableContactShadowLayer(model, shadowLayer);
  configureShadowCameraLayers(camera, shadowLayer);

  depthMaterial = new THREE.MeshDepthMaterial();
  depthMaterial.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      'gl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );',
      'gl_FragColor = vec4( vec3( 0.0 ), ( 1.0 - fragCoordZ ) * opacity );'
    );
  };
  depthMaterial.side = THREE.DoubleSide;

  renderContactShadowToPlane();
}

export function renderContactShadowToPlane() {
  if (!scene || !renderer || !floor || !camera || !renderTarget || !depthMaterial) {
    return;
  }

  const oldRenderTarget = renderer.getRenderTarget();
  const oldClearAlpha = renderer.getClearAlpha();
  const oldXrEnabled = renderer.xr.enabled;
  const oldSceneBackground = scene.background;

  try {
    scene.background = null;
    scene.overrideMaterial = depthMaterial;
    floor.visible = false;
    renderer.xr.enabled = false;
    renderer.setClearAlpha(0);
    renderer.setRenderTarget(renderTarget);
    renderer.render(scene, camera);
    blurShadowTexture();
    scene.overrideMaterial = null;
    floor.visible = true;
  } finally {
    renderer.xr.enabled = oldXrEnabled;
    renderer.setRenderTarget(oldRenderTarget);
    renderer.setClearAlpha(oldClearAlpha);
    scene.background = oldSceneBackground;
    scene.overrideMaterial = null;
    floor.visible = true;
  }
}

function blurShadowTexture() {
  if (
    shadowSoftness <= 0 ||
    !renderer ||
    !renderTarget ||
    !blurTarget ||
    !blurQuad ||
    !horizontalBlur ||
    !verticalBlur
  ) {
    return;
  }

  const blurStepX = (shadowSoftness * BLUR_RADIUS_PX) / renderTarget.width;
  const blurStepY = (shadowSoftness * BLUR_RADIUS_PX) / renderTarget.height;

  horizontalBlur.uniforms.tDiffuse.value = renderTarget.texture;
  horizontalBlur.uniforms.h.value = blurStepX;
  renderer.setRenderTarget(blurTarget);
  blurQuad.material = horizontalBlur;
  blurQuad.render(renderer);

  verticalBlur.uniforms.tDiffuse.value = blurTarget.texture;
  verticalBlur.uniforms.v.value = blurStepY;
  renderer.setRenderTarget(renderTarget);
  blurQuad.material = verticalBlur;
  blurQuad.render(renderer);
}

function disposeBlurResources() {
  blurTarget?.dispose();
  blurQuad?.dispose();
  horizontalBlur?.dispose();
  verticalBlur?.dispose();
  blurTarget = null;
  blurQuad = null;
  horizontalBlur = null;
  verticalBlur = null;
}

function clearContactShadowState() {
  scene = null;
  renderer = null;
  floor = null;
  camera = null;
  renderTarget = null;
  depthMaterial = null;
  shadowSoftness = 0;
  disposeBlurResources();
}



function collectShadowTargets(modelRoot: Object3D): Object3D[] {
  const targets: Object3D[] = [];

  function visit(parent: Object3D) {
    for (const child of parent.children) {
      if (child.userData.isSceneGroup) {
        visit(child);
      } else if (child.userData.isSceneObject) {
        targets.push(child);
      }
    }
  }

  visit(modelRoot);
  return targets;
}

function removeExistingShadowPlane(modelRoot: Object3D) {
  const existing = modelRoot.getObjectByName(SHADOW_PLANE_NAME);
  if (!existing || !(existing instanceof THREE.Mesh)) return;

  depthMaterial?.dispose();
  camera?.removeFromParent();
  clearContactShadowState();

  const existingRenderTarget = existing.userData.contactShadowRenderTarget as
    | THREE.WebGLRenderTarget
    | undefined;
  existingRenderTarget?.dispose();
  const material = existing.material;
  if (Array.isArray(material)) {
    for (const entry of material) entry.dispose();
  } else {
    material.dispose();
  }
  existing.geometry.dispose();
  existing.removeFromParent();
}

function enableContactShadowLayer(model: Object3D, layer: number) {
  model.traverse((object) => {
    if (object.name === SHADOW_PLANE_NAME) return;
    object.layers.enable(layer);
  });
}

function configureShadowCameraLayers(camera: THREE.OrthographicCamera, layer: number) {
  camera.layers.disableAll();
  camera.layers.enable(layer);
}

function computeWorldBounds(modelRoot: Object3D) {
  const bounds = new THREE.Box3();
  const transformed = new THREE.Box3();

  modelRoot.updateMatrixWorld(true);

  modelRoot.traverse((object) => {
    const mesh = object as THREE.Mesh;
    const geometry = mesh.geometry;
    if (!geometry || !('isBufferGeometry' in geometry) || !geometry.isBufferGeometry) return;

    // Skinned meshes deform on the GPU, so the geometry's (bind-pose) bounding box does
    // not reflect the visible silhouette. Use the mesh-level bounding box, which accounts
    // for the current skeleton pose.
    let localBox: THREE.Box3 | null;
    const skinned = mesh as THREE.SkinnedMesh;
    if (skinned.isSkinnedMesh) {
      skinned.computeBoundingBox();
      localBox = skinned.boundingBox;
    } else {
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      localBox = geometry.boundingBox;
    }
    if (!localBox) return;

    transformed.copy(localBox).applyMatrix4(object.matrixWorld);
    bounds.union(transformed);
  });

  return bounds;
}
